import { Component } from '@angular/core';
import { BaseBPProfilesDetailComponent } from '../base-bp-profiles-detail.component';
import { FormlyType, GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { BPProfilesService } from '../../bp-profiles.service';
import { FormattingService } from '@app/shared/services/formatting.service';
import { Override } from '@app/shared/app-decorators';
import { BPProfilesAcslInfoDataService, IAcslInfo } from './bp-profiles-acsl-info-data.service';
import _ from 'underscore';
import { LovService } from '@app/shared/services/lov.service';
import { RankingLabelComponent } from './ranking-label.component';
import { Subscription } from 'rxjs';
import { ContactDataService } from '@app/shared/contact/contact-data.service';

@Component({
  selector: 'bp-profiles-acsl-info',
  template: `
    <h2 *ngIf="loading"><activity-indicator></activity-indicator></h2>
    <formly [form]="form" [fields]="fields" [model]="model" *ngIf="!loading"></formly>
  `
})
export class BPProfilesAcslInfoComponent extends BaseBPProfilesDetailComponent {
  private backupAcslInfoData:IAcslInfo;
  private rankingServicesField:IExtraFieldConfigurationObject;
  private rankingField:IExtraFieldConfigurationObject;
  private coverageStatusField:IExtraFieldConfigurationObject;
  private gbdmField:IExtraFieldConfigurationObject;
  private pssField:IExtraFieldConfigurationObject;
  private checkAuthorField:IExtraFieldConfigurationObject;
  private bpTypesField:IExtraFieldConfigurationObject;
  private valueChangesSubscription:Subscription;

  constructor(
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private bpProfilesAcslInfoDataService:BPProfilesAcslInfoDataService,
    private lovService:LovService,
    private contactDataService:ContactDataService,
    protected bpProfilesService:BPProfilesService
  ) {
    super(bpProfilesService);
  }

  @Override(BaseBPProfilesDetailComponent)
  public ngOnInit():void {
    this.loading = true;
    this.bpProfilesAcslInfoDataService.getAcslInfo(this.bpProfilesService.activeProfile?.id)
      .then((acslInfo:IAcslInfo) => {
        this.backupAcslInfoData = _.clone(acslInfo);
        this.model = _.clone(acslInfo);
        // create fields
        this.fields = this.createFields();
        this.setRankingLabelComponentInputs(this.model);
      })
      .finally(() => {
        this.loading = false;
      });

    // register listener
    this.listen();
  }

  @Override(BaseBPProfilesDetailComponent)
  public listen():void {
    super.listen();
    this.valueChangesSubscription = this.form.valueChanges.subscribe(this.setRankingLabelComponentInputs);
  }

  @Override(BaseBPProfilesDetailComponent)
  public resetModel():void {
    if (this.backupAcslInfoData) {
      this.form.patchValue(this.backupAcslInfoData);
      this.form.markAsPristine();
    }
  }

  private setRankingLabelComponentInputs = (model:IAcslInfo):void => {
    if (!this.bpProfilesService.editingMode) {
      this.rankingField.templateOptions.inputs.ranking = model.ranking;
      this.rankingServicesField.templateOptions.inputs.ranking = model.rankingServices;
    }
  };

  protected async fillFields():Promise<void> {
    await this.formlyService.fillField([this.rankingServicesField, this.rankingField], this.lovService.list('DOPREPA'));
    await this.formlyService.fillField(this.coverageStatusField, this.lovService.list('DOCOVST'));
    await this.formlyService.fillField(this.bpTypesField, this.lovService.list('DOBPTYPE'));
    await this.formlyService.fillField([this.gbdmField, this.pssField, this.checkAuthorField], this.contactDataService.getAcslActiveContacts());
  }

  protected createFields():IExtraFieldConfigurationObject[] {
    const editingMode:boolean = this.bpProfilesService.editingMode;

    if (editingMode) {
      this.rankingField = this.formlyService.aSelect('ranking')
        .withFormat(this.formattingService.formatLov)
        .build();

      this.rankingServicesField = this.formlyService.aSelect('rankingServices')
        .withFormat(this.formattingService.formatLov)
        .build();
    } else {
      this.rankingField = this.formlyService.aCustom()
        .withKey('ranking')
        .withComponent(RankingLabelComponent)
        .withFormat(this.formattingService.formatLov)
        .withExtraOptions({
          templateOptions: {
            inputs: {
              identifier: 'ranking'
            }
          }
        })
        .build();

      this.rankingServicesField = this.formlyService.aCustom()
        .withKey('rankingServices')
        .withComponent(RankingLabelComponent)
        .withFormat(this.formattingService.formatLov)
        .withExtraOptions({
          templateOptions: {
            inputs: {
              identifier: 'rankingServices'
            }
          }
        })
        .build();
    }

    return [
      this.formlyService.aTitle('BPPROFILES.ACSL_INFO.ACSLINFO.TITLE')
        .build(),
      this.pssField = this.formlyService.aField(editingMode? FormlyType.SELECT : FormlyType.VALUE, 'od')
        .withFormat(this.formattingService.formatContact)
        .build(),
      this.formlyService.aField(FormlyType.VALUE, 'bpDeskTeamLead')
        .withFormat(this.formattingService.formatContact)
        .build(),
      this.gbdmField = this.formlyService.aField(editingMode? FormlyType.SELECT : FormlyType.VALUE, 'gbdm')
        .withFormat(this.formattingService.formatContact)
        .build(),
      this.rankingField,
      this.rankingServicesField,
      this.formlyService.aField(editingMode? FormlyType.CHECKBOX : FormlyType.VALUE, 'operational')
        .withFormat(this.formattingService.formatBoolean)
        .build(),
      this.formlyService.aField(editingMode? FormlyType.INPUT : FormlyType.VALUE, 'timeSlot')
        .withPlaceholder('Timeslot')
        .build(),
      this.formlyService.aField(editingMode? FormlyType.CHECKBOX : FormlyType.VALUE, 'fullyResponsive')
        .withFormat(this.formattingService.formatBoolean)
        .build(),
      this.formlyService.aField(editingMode? FormlyType.CHECKBOX : FormlyType.VALUE, 'updatesGA')
        .withFormat(this.formattingService.formatBoolean)
        .build(),
      this.formlyService.aField(editingMode? FormlyType.CHECKBOX : FormlyType.VALUE, 'qualityKPI')
        .withFormat(this.formattingService.formatBoolean)
        .build(),
      this.formlyService.aValue('progress')
        .withFormat(this.formattingService.formatPct)
        .build(),
      this.bpTypesField = this.formlyService.aField(editingMode? FormlyType.NG_SELECT : FormlyType.VALUE, 'bpTypes')
        .withFormat(this.formattingService.formatLov)
        .withExtraOptions({
          templateOptions: {
            localSearch: true,
            multiple: true
          }
        })
        .build(),
      this.coverageStatusField = this.formlyService.aField(editingMode? FormlyType.SELECT : FormlyType.VALUE, 'coverageStatus')
        .withFormat(this.formattingService.formatLov)
        .build(),
      this.formlyService.aField(editingMode? FormlyType.TEXTAREA : FormlyType.VALUE, 'coverageComment')
        .build(),
      //-- GA Settings
      this.formlyService.aTitle('BPPROFILES.ACSL_INFO.GASETTINGS.TITLE')
        .build(),
      this.formlyService.aField(editingMode? FormlyType.CHECKBOX : FormlyType.VALUE, 'autoCloseOrders')
        .withFormat(this.formattingService.formatBoolean)
        .build(),
      this.formlyService.aField(editingMode? FormlyType.CHECKBOX : FormlyType.VALUE, 'approvalForNewWebshops')
        .withFormat(this.formattingService.formatBoolean)
        .build(),
      //-- Comments
      this.formlyService.aTitle('BPPROFILES.ACSL_INFO.COMMENTS.TITLE')
        .build(),
      this.formlyService.aField(editingMode? FormlyType.TEXTAREA : FormlyType.VALUE, 'weekUrgencies')
        .build(),
      this.formlyService.aField(editingMode? FormlyType.TEXTAREA : FormlyType.VALUE, 'weekOpenItems')
        .build(),
      this.formlyService.aField(editingMode? FormlyType.TEXTAREA : FormlyType.VALUE, 'success')
        .build(),
      this.formlyService.aField(editingMode? FormlyType.TEXTAREA : FormlyType.VALUE, 'strengths')
        .build(),
      this.formlyService.aField(editingMode? FormlyType.TEXTAREA : FormlyType.VALUE, 'weaknesses')
        .build(),
      this.formlyService.aField(editingMode? FormlyType.TEXTAREA : FormlyType.VALUE, 'companyInfo')
        .build(),
      this.formlyService.aField(editingMode? FormlyType.TEXTAREA : FormlyType.VALUE, 'backupInfo')
        .build(),
      this.formlyService.aField(editingMode? FormlyType.TEXTAREA : FormlyType.VALUE, 'swot')
        .build(),
      this.formlyService.aField(editingMode? FormlyType.TEXTAREA : FormlyType.VALUE, 'specificInstructions')
        .build(),
      this.formlyService.aField(editingMode? FormlyType.TEXTAREA : FormlyType.VALUE, 'bpAssessment')
        .build(),
      //-- Intel desk check
      this.formlyService.aTitle('BPPROFILES.ACSL_INFO.INTELDESKCHECK.TITLE')
        .build(),
      this.formlyService.aField(editingMode? FormlyType.DATE_INPUT : FormlyType.VALUE, 'checkDate')
        .withFormat(this.formattingService.formatDate)
        .build(),
      this.checkAuthorField = this.formlyService.aField(editingMode? FormlyType.SELECT : FormlyType.VALUE, 'checkAuthor')
        .withFormat(this.formattingService.formatContact)
        .build(),
      this.formlyService.aField(editingMode? FormlyType.TEXTAREA : FormlyType.VALUE, 'checkComments')
        .withMaxLength(4000)
        .build()
    ];
  }

  protected onSave = ():void => {
    this.setSaveBusy(true);
    this.bpProfilesAcslInfoDataService.saveAcslInfo(this.bpProfilesService.activeProfile?.id, this.model)
      .then((acslInfo:IAcslInfo) => {
        this.backupAcslInfoData = _.clone(acslInfo);
      })
      .finally(() => {
        this.setSaveBusy(false);
      });
  };

  @Override(BaseBPProfilesDetailComponent)
  public ngOnDestroy():void {
    super.ngOnDestroy();
    this.valueChangesSubscription?.unsubscribe();
  }
}